import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { TrendingUp, AlertTriangle, Lightbulb, DollarSign, Target, Activity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RevenueForecast = () => {
  const { token } = useAuth();
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchForecast();
  }, []);

  const fetchForecast = async () => {
    try {
      const response = await axios.get(`${API}/forecast/revenue?token=${token}`);
      setForecast(response.data);
    } catch (error) {
      console.error('Failed to fetch forecast', error);
      toast.error('Failed to load revenue forecast');
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (health) => {
    if (health === 'healthy') return 'bg-success';
    if (health === 'at-risk') return 'bg-warning';
    return 'bg-error';
  };

  const getHealthIcon = (health) => {
    if (health === 'healthy') return <Activity className="w-5 h-5 text-success" />;
    if (health === 'at-risk') return <AlertTriangle className="w-5 h-5 text-warning" />;
    return <AlertTriangle className="w-5 h-5 text-error" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="forecast-loading">
        <p className="text-slate-500">Generating AI forecast...</p>
      </div>
    );
  }

  if (!forecast) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-500">Unable to generate forecast</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="forecast-page">
      <div>
        <h3 className="text-2xl font-heading font-bold text-primary">AI Revenue Forecasting</h3>
        <p className="text-sm text-slate-500 mt-1">Predictive analytics powered by AI</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="kpi-card" data-testid="kpi-predicted-revenue">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Predicted Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-heading font-light text-primary">
              AED {(forecast.predicted_revenue || 0).toLocaleString()}
            </div>
            <p className="text-xs text-slate-500 mt-1">Next month forecast</p>
          </CardContent>
        </Card>

        <Card className="kpi-card" data-testid="kpi-confidence">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Confidence Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-heading font-light text-accent">
              {forecast.confidence_score || 0}%
            </div>
            <Progress value={forecast.confidence_score || 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card className="kpi-card" data-testid="kpi-pipeline-health">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Pipeline Health</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mt-2">
              {getHealthIcon(forecast.pipeline_health)}
              <Badge className={`${getHealthColor(forecast.pipeline_health)} text-white text-sm capitalize`}>
                {forecast.pipeline_health || 'unknown'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card" data-testid="kpi-active-deals">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Active Deals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-heading font-light text-info">
              {forecast.active_deals || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">In pipeline</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="risk-factors">
          <CardHeader className="flex flex-row items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-error" />
            <CardTitle className="font-heading text-lg">Risk Factors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {forecast.risk_factors && forecast.risk_factors.length > 0 ? (
                forecast.risk_factors.map((risk, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 bg-error/5 border border-error/20 rounded-lg"
                  >
                    <AlertTriangle className="w-4 h-4 text-error mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-slate-700">{risk}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">No significant risks identified</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="opportunities">
          <CardHeader className="flex flex-row items-center gap-2">
            <Lightbulb className="w-5 h-5 text-warning" />
            <CardTitle className="font-heading text-lg">Opportunities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {forecast.opportunities && forecast.opportunities.length > 0 ? (
                forecast.opportunities.map((opportunity, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 bg-success/5 border border-success/20 rounded-lg"
                  >
                    <Lightbulb className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-slate-700">{opportunity}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">No opportunities identified</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="pipeline-metrics">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Pipeline Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-info/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-info" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Pipeline Value</p>
                <p className="text-xl font-semibold text-primary">
                  AED {(forecast.pipeline_value || 0).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                <Target className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Weighted Pipeline</p>
                <p className="text-xl font-semibold text-primary">
                  AED {(forecast.weighted_value || 0).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-success" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Avg Deal Size</p>
                <p className="text-xl font-semibold text-primary">
                  AED {(forecast.avg_deal_size || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RevenueForecast;